// src/hooks/useCollapsibleSections.js

import { useState, useCallback } from 'react';

/**
 * Hook per gestire sezioni collassabili con supporto accordion
 * 
 * @param {Array} sections - Array di ID sezioni
 * @param {Object} options - Opzioni configurazione
 * @param {boolean} options.accordionMode - Se true, solo una sezione aperta alla volta
 * @param {string} options.defaultOpen - ID sezione aperta di default
 * @param {Array} options.defaultCollapsed - Array di ID sezioni chiuse di default
 * @returns {Object} - Funzioni e stato per gestire le sezioni
 */
export const useCollapsibleSections = (sections = [], options = {}) => {
  const {
    accordionMode = false,
    defaultOpen = null,
    defaultCollapsed = []
  } = options;

  // Inizializza lo stato delle sezioni
  const [collapsedSections, setCollapsedSections] = useState(() => {
    const initialSet = new Set();

    if (accordionMode) {
      // In modalità accordion, chiudi tutte tranne defaultOpen
      sections.forEach(section => {
        if (section !== defaultOpen) {
          initialSet.add(section);
        }
      });
    } else {
      // In modalità normale, usa defaultCollapsed
      defaultCollapsed.forEach(section => {
        initialSet.add(section);
      });
    }

    return initialSet;
  });

  /**
   * Toggle una sezione (apri/chiudi)
   */
  const toggleSection = useCallback((sectionId) => {
    setCollapsedSections(prev => {
      const newSet = new Set(prev);
      const isCurrentlyCollapsed = newSet.has(sectionId);

      if (accordionMode) {
        if (isCurrentlyCollapsed) {
          // ✅ ACCORDION: Chiudi tutte le altre, apri questa
          sections.forEach(s => newSet.add(s));
          newSet.delete(sectionId);
        } else {
          // ❌ In accordion mode, non permettere di chiudere l'unica sezione aperta
          // (opzionale: puoi rimuovere questo per permettere di chiudere tutto)
          return prev;
        }
      } else {
        // 🔄 NORMAL MODE: Toggle semplice
        if (isCurrentlyCollapsed) {
          newSet.delete(sectionId);
        } else {
          newSet.add(sectionId);
        }
      }

      return newSet;
    });
  }, [accordionMode, sections]);

  /**
   * Verifica se una sezione è collassata
   */
  const isSectionCollapsed = useCallback((sectionId) => {
    return collapsedSections.has(sectionId);
  }, [collapsedSections]);

  /**
   * Espandi tutte le sezioni
   */
  const expandAllSections = useCallback(() => {
    setCollapsedSections(new Set());
  }, []);

  /**
   * Collassa tutte le sezioni
   */
  const collapseAllSections = useCallback(() => {
    setCollapsedSections(new Set(sections));
  }, [sections]);

  /**
   * Apri una sezione specifica (utile per navigazione)
   */
  const openSection = useCallback((sectionId) => {
    setCollapsedSections(prev => {
      const newSet = new Set(prev);

      if (accordionMode) {
        // In accordion, chiudi tutte le altre
        sections.forEach(s => newSet.add(s));
      }

      newSet.delete(sectionId);
      return newSet;
    });
  }, [accordionMode, sections]);

  /**
   * Chiudi una sezione specifica
   */
  const closeSection = useCallback((sectionId) => {
    setCollapsedSections(prev => {
      const newSet = new Set(prev);
      newSet.add(sectionId);
      return newSet;
    });
  }, []);

  /**
   * Ottieni la sezione successiva
   */
  const getNextSection = useCallback((currentSectionId) => {
    const currentIndex = sections.indexOf(currentSectionId);
    if (currentIndex === -1 || currentIndex === sections.length - 1) {
      return null;
    }
    return sections[currentIndex + 1];
  }, [sections]);

  /**
   * Ottieni la sezione precedente
   */
  const getPreviousSection = useCallback((currentSectionId) => {
    const currentIndex = sections.indexOf(currentSectionId);
    if (currentIndex <= 0) {
      return null;
    }
    return sections[currentIndex - 1];
  }, [sections]);

  /**
   * Naviga alla sezione successiva
   */
  const goToNextSection = useCallback((currentSectionId) => {
    const nextSection = getNextSection(currentSectionId);
    if (nextSection) {
      openSection(nextSection);
      
      // Scroll alla sezione (opzionale)
      setTimeout(() => {
        const element = document.querySelector(`[data-section-id="${nextSection}"]`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, [getNextSection, openSection]);

  /**
   * Naviga alla sezione precedente
   */
  const goToPreviousSection = useCallback((currentSectionId) => {
    const previousSection = getPreviousSection(currentSectionId);
    if (previousSection) {
      openSection(previousSection);
      
      // Scroll alla sezione (opzionale)
      setTimeout(() => {
        const element = document.querySelector(`[data-section-id="${previousSection}"]`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, [getPreviousSection, openSection]);

  /**
   * Ottieni la prima sezione aperta
   */
  const getOpenSection = useCallback(() => {
    return sections.find(section => !collapsedSections.has(section)) || null;
  }, [sections, collapsedSections]);

  /**
   * Ottieni il conteggio sezioni aperte
   */
  const getOpenSectionsCount = useCallback(() => {
    return sections.filter(section => !collapsedSections.has(section)).length;
  }, [sections, collapsedSections]);

  /**
   * Ottieni il conteggio sezioni chiuse
   */
  const getCollapsedSectionsCount = useCallback(() => {
    return collapsedSections.size;
  }, [collapsedSections]);

  return {
    // Stato
    collapsedSections,
    
    // Funzioni principali
    toggleSection,
    isSectionCollapsed,
    expandAllSections,
    collapseAllSections,
    openSection,
    closeSection,
    
    // Navigazione
    getNextSection,
    getPreviousSection,
    goToNextSection,
    goToPreviousSection,
    getOpenSection,
    
    // Info/Stats
    getOpenSectionsCount,
    getCollapsedSectionsCount,
    totalSections: sections.length,
    
    // Opzioni
    isAccordionMode: accordionMode
  };
};

export default useCollapsibleSections;